import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Lightweight HLS preview player for the admin Tools page.
 *
 * Mixed-content note:
 *   The admin panel is served over HTTPS. Browsers block HTTP media subresources
 *   (m3u8/ts) loaded inside an HTTPS page — this affects every IPTV provider
 *   that serves on plain HTTP. There is no client-side fix; the operator must
 *   either (a) use HTTPS upstreams, (b) hand off to a desktop player like VLC
 *   (see "VLC ile Aç" button), or (c) terminate TLS at an edge node proxy.
 *   We surface a clear error when the load fails for this reason.
 */
export default function HlsPreviewPlayer({ url }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [status, setStatus] = useState("idle");      // 'idle' | 'loading' | 'ready' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  const stop = () => {
    try { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } } catch { /* noop */ }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
    setStatus("idle"); setErrorMsg("");
  };

  useEffect(() => () => stop(), []);

  const play = () => {
    stop();
    if (!url) return;
    setErrorMsg(""); setStatus("loading");

    // Mixed-content pre-check: short-circuit with a clear error
    const isInsecure = url.startsWith("http://");
    const onSecurePage = window.location.protocol === "https:";
    if (isInsecure && onSecurePage) {
      setStatus("error");
      setErrorMsg(
        "Tarayıcı HTTPS sayfasından HTTP kaynağa erişimi engelliyor (mixed content). " +
        "Yandaki 'VLC ile Aç' butonu ile masaüstü oynatıcıda açın veya kaynağı HTTPS'e taşıyın."
      );
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    // Native Safari HLS path
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
      video.addEventListener("loadedmetadata", () => { setStatus("ready"); video.play().catch(() => {}); }, { once: true });
      video.addEventListener("error", () => { setStatus("error"); setErrorMsg("Video yüklenemedi (Safari path)."); }, { once: true });
      return;
    }

    // hls.js path
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false, capLevelToPlayerSize: true });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { setStatus("ready"); video.play().catch(() => {}); });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          setStatus("error");
          setErrorMsg(
            data.type === "networkError"
              ? "Ağ hatası — URL erişilemiyor veya CORS engeli (mixed content olabilir)."
              : data.type === "mediaError"
              ? "Medya hatası — codec uyumsuz veya stream bozuk."
              : `Oynatma hatası: ${data.details || "bilinmeyen"}.`
          );
          stop();
        }
      });
      return;
    }

    setStatus("error");
    setErrorMsg("Bu tarayıcı HLS desteklemiyor.");
  };

  const isHttpInsecure = url?.startsWith("http://") && window.location.protocol === "https:";

  return (
    <div className="space-y-3">
      <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-slate-800">
        <video
          ref={videoRef}
          controls
          playsInline
          className="w-full h-full object-contain bg-black"
          data-testid="tools-preview-video"
        />
        {status === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-500 pointer-events-none">
            <Play size={32} className="opacity-50" />
            <span className="text-xs uppercase tracking-wider font-mono">Stream URL girin & "Oynat"</span>
          </div>
        )}
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <Loader2 className="animate-spin text-amber-500" size={36} />
          </div>
        )}
      </div>

      {isHttpInsecure && status === "idle" && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-300/90">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            Bu URL <span className="font-mono">http://</span> ile başlıyor. Tarayıcı içinde oynamayabilir
            (mixed content). "<strong>VLC ile Aç</strong>" butonunu kullanın.
          </span>
        </div>
      )}

      {status === "error" && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs" data-testid="tools-preview-error">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" /> <span>{errorMsg}</span>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={play}
          disabled={!url || status === "loading"}
          className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold py-2.5 rounded-xl transition flex items-center justify-center gap-2"
          data-testid="tools-preview-play"
        >
          <Play size={16} /> {status === "loading" ? "Yükleniyor…" : "Oynat"}
        </button>
        <button
          type="button"
          onClick={stop}
          disabled={status === "idle"}
          className="px-4 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-white font-bold py-2.5 rounded-xl transition"
          data-testid="tools-preview-stop"
        >
          Durdur
        </button>
      </div>
    </div>
  );
}
